import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import { movies, hotSearches } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 10;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").trim();
  const pageParam = Number(searchParams.get("page") || "1");
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useSeo({
    title: query
      ? `「${query}」搜索结果 - 日韩伦理电影`
      : "站内搜索 - 日韩伦理电影资源与剧情介绍",
    description: query
      ? `在日韩伦理电影资料站中搜索「${query}」，查看匹配影片的年份、地区、导演、主演与剧情介绍。`
      : "输入片名、导演或关键词，快速检索日韩伦理电影资料，支持标题模糊匹配与分类筛选。",
    keywords: query
      ? `${query},日韩伦理电影搜索,${query}剧情介绍,电影资源搜索`
      : "日韩伦理电影搜索,影片检索,剧情介绍,电影资源",
  });

  const results = useMemo(() => {
    if (!query) return [];
    const lower = query.toLowerCase();
    return movies
      .filter((movie) => {
        const fields = [
          movie.title,
          movie.originalTitle,
          movie.director,
          movie.country,
          ...movie.cast,
          ...movie.genres,
          ...movie.tags,
          String(movie.year),
        ];
        return fields.some((field) => field.toLowerCase().includes(lower));
      })
      .sort((a, b) => b.year - a.year || b.popularity - a.popularity);
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, pageParam || 1), totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setSearchParams(value ? { q: value } : {});
  };

  const goPage = (page: number) => {
    setSearchParams({ q: query, page: String(page) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200/70 bg-accent-950">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-10 pt-7 md:px-8 md:pb-12 md:pt-9">
          <nav aria-label="面包屑" className="mb-6 flex items-center gap-1.5 font-label text-[11px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-300">首页</Link>
            <i className="ri-arrow-right-s-line text-[13px]"></i>
            <span className="text-primary-300">站内搜索</span>
          </nav>

          <h1 className="font-heading text-2xl font-semibold leading-tight text-foreground-950 md:text-[32px]">
            全站影片搜索
          </h1>
          <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-foreground-500">
            支持片名模糊匹配，也可以输入导演、演员、年份或标签关键词。搜索结果仅基于站内影片资料。
          </p>

          <form onSubmit={submitSearch} className="relative mt-6 flex max-w-2xl flex-col gap-2 sm:flex-row" role="search">
            <div className="relative min-w-0 flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-500"></i>
              <input
                type="text"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="例如：春夜之约、京都、经典"
                aria-label="搜索关键词"
                className="h-12 w-full rounded-md border border-background-300/70 bg-background-100 pl-11 pr-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-500"
              />
            </div>
            <button
              type="submit"
              className="inline-flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-500"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="font-label text-[11px] text-foreground-500">热门搜索</span>
            {hotSearches.map((item) => (
              <Link
                key={item}
                to={`/search?q=${encodeURIComponent(item)}`}
                className="rounded-md border border-accent-800/60 bg-accent-900/40 px-2.5 py-1 text-[11px] text-foreground-500 transition-colors hover:border-primary-500 hover:text-primary-200"
              >
                {item}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-9 md:px-8 md:py-12">
        {query ? (
          <>
            <div className="mb-6 flex flex-col gap-2 border-b border-background-200/70 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[13px] text-foreground-600">
                关键词 <span className="mx-1 rounded bg-primary-950 px-2 py-0.5 text-primary-200">「{query}」</span>
                共匹配 <span className="font-semibold text-secondary-300">{results.length}</span> 部影片
              </p>
              {results.length > 0 && (
                <p className="font-label text-[11px] text-foreground-500">
                  第 {currentPage} / {totalPages} 页
                </p>
              )}
            </div>

            {pageItems.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300/70 bg-background-100/40 px-4 py-16 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-background-300/60 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                  没有找到与「{query}」相关的影片
                </h2>
                <p className="mt-2 text-[12.5px] text-foreground-500">
                  试试更短的关键词，或者从下面的分类与热门搜索里继续找。
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {categories.map((category) => (
                    <Link
                      key={category.key}
                      to={`/category/${category.key}`}
                      className="rounded-md border border-background-300/70 px-3 py-1.5 text-[11.5px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
                    >
                      {category.label}电影
                    </Link>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {hotSearches.slice(0, 4).map((item) => (
                    <Link
                      key={item}
                      to={`/search?q=${encodeURIComponent(item)}`}
                      className="rounded-md bg-background-200/70 px-2.5 py-1 text-[11px] text-foreground-500 transition-colors hover:text-primary-200"
                    >
                      {item}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-10">
                <Pagination page={currentPage} totalPages={totalPages} onChange={goPage} />
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-xl font-semibold text-foreground-950">从分类开始浏览</h2>
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-500">
                还没有想好搜什么？先按类型进去看看，每个分类都有独立的筛选与排序。
              </p>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {categories.map((category) => (
                  <Link
                    key={category.key}
                    to={`/category/${category.key}`}
                    className="group flex items-start gap-3 rounded-lg border border-background-200/80 bg-background-100 p-4 transition-colors hover:border-primary-500/70"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-900/60 text-accent-200">
                      <i className={`${category.icon} text-base`}></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13.5px] font-medium text-foreground-800 group-hover:text-primary-200">
                        {category.label}电影
                      </span>
                      <span className="mt-0.5 block text-[11.5px] text-foreground-500">{category.tagline}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-lg border border-background-200/80 bg-background-100 p-5">
                <h2 className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-foreground-800">
                  <i className="ri-fire-line text-primary-300"></i>
                  热度最高的六部
                </h2>
                <ol className="divide-y divide-background-200/70">
                  {[...movies]
                    .sort((a, b) => b.popularity - a.popularity)
                    .slice(0, 6)
                    .map((movie, index) => (
                      <li key={movie.id}>
                        <Link to={`/detail/${movie.id}`} className="group flex items-center gap-3 py-2.5">
                          <span className="w-4 font-heading text-sm font-semibold text-primary-400">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-[12.5px] text-foreground-700 group-hover:text-primary-200">
                            {movie.title}
                          </span>
                          <span className="font-label text-[11px] text-foreground-500">{movie.year}</span>
                        </Link>
                      </li>
                    ))}
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}